import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiLayers, FiSearch, FiFilter, FiCheckCircle, FiPlay,
  FiServer, FiX, FiPlus, FiFolder, FiLoader, FiChevronRight,
} from 'react-icons/fi';
import { SiAmazon, SiGooglecloud } from 'react-icons/si';
import DashboardLayout from '../Dashboard/DashboardLayout';
import {
  ALL_TEMPLATES, PROVIDER_LABELS, CATEGORY_LABELS, DIFFICULTY_LABELS,
  TemplateData, TemplateProvider, TemplateCategory, TemplateDifficulty,
} from './templateData';
import { getProjects, createProject, Project } from '../../api/projects';
import { createArchitecture } from '../../api/architectures';

// ─── Icônes provider ────────────────────────────────────────────────────────

const ProviderIcon: React.FC<{ provider: TemplateProvider; size?: number }> = ({ provider, size = 20 }) => {
  switch (provider) {
    case 'azure':      return <FiServer className="text-blue-600" size={size} />;
    case 'aws':        return <SiAmazon className="text-orange-500" size={size} />;
    case 'gcp':        return <SiGooglecloud className="text-blue-500" size={size} />;
    case 'fabric':     return <FiLayers className="text-purple-600" size={size} />;
    case 'multicloud': return <FiLayers className="text-indigo-600" size={size} />;
  }
};

const PROVIDER_COLORS: Record<TemplateProvider, string> = {
  azure:      'bg-blue-50 text-blue-700 border-blue-300',
  aws:        'bg-orange-50 text-orange-700 border-orange-300',
  gcp:        'bg-sky-50 text-sky-700 border-sky-300',
  fabric:     'bg-purple-50 text-purple-700 border-purple-300',
  multicloud: 'bg-indigo-50 text-indigo-700 border-indigo-300',
};

const DIFFICULTY_COLORS: Record<TemplateDifficulty, string> = {
  beginner:     'bg-green-100 text-green-700',
  intermediate: 'bg-yellow-100 text-yellow-700',
  advanced:     'bg-red-100 text-red-700',
};

// ─── Composant principal ─────────────────────────────────────────────────────

const TemplatesPage: React.FC = () => {
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm]             = useState('');
  const [providerFilter, setProviderFilter]     = useState<TemplateProvider | 'all'>('all');
  const [categoryFilter, setCategoryFilter]     = useState<TemplateCategory | 'all'>('all');
  const [difficultyFilter, setDifficultyFilter] = useState<TemplateDifficulty | 'all'>('all');

  const [previewTemplate, setPreviewTemplate] = useState<TemplateData | null>(null);
  const [useStep, setUseStep]                 = useState<'none' | 'preview' | 'project-select'>('none');

  const openPreview = useCallback((t: TemplateData) => {
    setPreviewTemplate(t);
    setUseStep('preview');
  }, []);

  const openUseFlow = useCallback((t: TemplateData) => {
    setPreviewTemplate(t);
    setUseStep('project-select');
  }, []);

  const closeModal = useCallback(() => {
    setUseStep('none');
    setPreviewTemplate(null);
  }, []);

  // Filtrage
  const filtered = ALL_TEMPLATES.filter(tpl => {
    if (providerFilter  !== 'all' && tpl.provider  !== providerFilter)  return false;
    if (categoryFilter  !== 'all' && tpl.category  !== categoryFilter)  return false;
    if (difficultyFilter !== 'all' && tpl.difficulty !== difficultyFilter) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      return tpl.name.toLowerCase().includes(q) || tpl.description.toLowerCase().includes(q) ||
             tpl.tags.some(tag => tag.toLowerCase().includes(q));
    }
    return true;
  });

  const providers: Array<TemplateProvider | 'all'> = ['all', 'azure', 'aws', 'gcp', 'fabric', 'multicloud'];
  const categories = Array.from(new Set(ALL_TEMPLATES.map(t => t.category)));
  const difficulties: TemplateDifficulty[] = ['beginner', 'intermediate', 'advanced'];

  return (
    <DashboardLayout>
      <div className="p-8">
        {/* En-tête */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Bibliothèque de templates</h1>
          <p className="text-gray-600">
            Démarrez avec une architecture préconstruite pour Azure, AWS, Google Cloud ou Microsoft Fabric.
          </p>
        </div>

        {/* Barre de recherche */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
          <div className="relative mb-4">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Rechercher un template, un service, un tag…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Filtre Provider (chips) */}
          <div className="flex flex-wrap gap-2 mb-4">
            {providers.map(p => {
              const active = providerFilter === p;
              return (
                <button
                  key={p}
                  onClick={() => setProviderFilter(p)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition
                    ${active
                      ? p === 'all'
                        ? 'bg-gray-900 text-white border-gray-900'
                        : `${PROVIDER_COLORS[p as TemplateProvider]} border font-semibold`
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                    }`}
                >
                  {p !== 'all' && <ProviderIcon provider={p as TemplateProvider} size={14} />}
                  {p === 'all' ? 'Tous les clouds' : PROVIDER_LABELS[p as TemplateProvider]}
                </button>
              );
            })}
          </div>

          {/* Filtres secondaires */}
          <div className="flex flex-wrap gap-3">
            <select
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value as TemplateCategory | 'all')}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white text-gray-700 focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Toutes les catégories</option>
              {categories.map(c => (
                <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
              ))}
            </select>

            <select
              value={difficultyFilter}
              onChange={e => setDifficultyFilter(e.target.value as TemplateDifficulty | 'all')}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white text-gray-700 focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Toutes les difficultés</option>
              {difficulties.map(d => (
                <option key={d} value={d}>{DIFFICULTY_LABELS[d]}</option>
              ))}
            </select>

            {(providerFilter !== 'all' || categoryFilter !== 'all' || difficultyFilter !== 'all' || searchTerm) && (
              <button
                onClick={() => { setProviderFilter('all'); setCategoryFilter('all'); setDifficultyFilter('all'); setSearchTerm(''); }}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition"
              >
                <FiX size={14} /> Réinitialiser
              </button>
            )}
          </div>
        </div>

        {/* Compteur */}
        <p className="text-sm text-gray-500 mb-5">
          {filtered.length} template{filtered.length > 1 ? 's' : ''} disponible{filtered.length > 1 ? 's' : ''}
        </p>

        {/* Grille de templates */}
        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map(tpl => (
              <TemplateCard
                key={tpl.id}
                template={tpl}
                onPreview={() => openPreview(tpl)}
                onUse={() => openUseFlow(tpl)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <FiFilter size={56} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-1">Aucun template trouvé</h3>
            <p className="text-sm text-gray-500">Modifiez vos critères de recherche.</p>
          </div>
        )}

        {/* Modals */}
        {previewTemplate && useStep === 'preview' && (
          <PreviewModal
            template={previewTemplate}
            onClose={closeModal}
            onUse={() => setUseStep('project-select')}
          />
        )}

        {previewTemplate && useStep === 'project-select' && (
          <UseTemplateModal
            template={previewTemplate}
            onClose={closeModal}
            onSuccess={(projectId, archId) => {
              closeModal();
              navigate('/canvas', { state: { projectId, architectureId: archId } });
            }}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

// ─── Card template ──────────────────────────────────────────────────────────

const TemplateCard: React.FC<{
  template: TemplateData;
  onPreview: () => void;
  onUse: () => void;
}> = ({ template, onPreview, onUse }) => (
  <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-200 flex flex-col">
    {template.popular && (
      <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-bold px-4 py-1 text-center rounded-t-xl">
        ⭐ Populaire
      </div>
    )}
    <div className="p-5 flex flex-col flex-1">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <span className="text-4xl">{template.icon}</span>
        <span className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${PROVIDER_COLORS[template.provider]}`}>
          <ProviderIcon provider={template.provider} size={12} />
          {PROVIDER_LABELS[template.provider]}
        </span>
      </div>

      <h3 className="font-bold text-gray-900 mb-1">{template.name}</h3>
      <p className="text-sm text-gray-600 mb-3 flex-1 line-clamp-2">{template.description}</p>

      {/* Tags */}
      <div className="flex flex-wrap gap-1 mb-3">
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${DIFFICULTY_COLORS[template.difficulty]}`}>
          {DIFFICULTY_LABELS[template.difficulty]}
        </span>
        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
          {CATEGORY_LABELS[template.category]}
        </span>
      </div>

      {/* Stats */}
      <div className="flex justify-between text-xs text-gray-500 border-t border-gray-100 pt-3 mb-4">
        <span><strong className="text-gray-800">{template.resourcesCount}</strong> ressources</span>
        <span>Déploiement : <strong className="text-gray-800">{template.deploymentTime}</strong></span>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={onPreview}
          className="flex-1 px-3 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition text-sm font-medium"
        >
          Aperçu
        </button>
        <button
          onClick={onUse}
          className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium flex items-center justify-center gap-1"
        >
          <FiPlay size={13} /> Utiliser
        </button>
      </div>
    </div>
  </div>
);

// ─── Modal d'aperçu ──────────────────────────────────────────────────────────

const PreviewModal: React.FC<{
  template: TemplateData;
  onClose: () => void;
  onUse: () => void;
}> = ({ template, onClose, onUse }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-5xl">{template.icon}</span>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{template.name}</h2>
            <div className="flex items-center gap-1.5 mt-1">
              <ProviderIcon provider={template.provider} size={16} />
              <span className="text-sm text-gray-600">{PROVIDER_LABELS[template.provider]}</span>
              <span className="text-gray-300">•</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${DIFFICULTY_COLORS[template.difficulty]}`}>
                {DIFFICULTY_LABELS[template.difficulty]}
              </span>
            </div>
          </div>
        </div>
        <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition">
          <FiX size={20} />
        </button>
      </div>

      {/* Contenu */}
      <div className="p-6">
        <p className="text-gray-700 mb-6">{template.longDescription}</p>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 rounded-xl p-4 text-center">
            <div className="text-xl font-bold text-blue-600">{template.resourcesCount}</div>
            <div className="text-xs text-gray-500 mt-1">Ressources</div>
          </div>
          <div className="bg-green-50 rounded-xl p-4 text-center">
            <div className="text-xl font-bold text-green-600">{template.deploymentTime}</div>
            <div className="text-xs text-gray-500 mt-1">Déploiement</div>
          </div>
          <div className="bg-purple-50 rounded-xl p-4 text-center">
            <div className="text-xl font-bold text-purple-600">{CATEGORY_LABELS[template.category]}</div>
            <div className="text-xs text-gray-500 mt-1">Catégorie</div>
          </div>
        </div>

        {/* Services inclus */}
        <h3 className="font-semibold text-gray-900 mb-3">Services inclus</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
          {template.features.map((f, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
              <FiCheckCircle className="text-green-500 flex-shrink-0" size={16} />
              {f}
            </div>
          ))}
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-6">
          {template.tags.map(tag => (
            <span key={tag} className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">{tag}</span>
          ))}
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition text-sm font-medium">
            Fermer
          </button>
          <button
            onClick={onUse}
            className="flex-1 px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition text-sm font-medium flex items-center justify-center gap-2"
          >
            <FiPlay size={16} /> Utiliser ce template
          </button>
        </div>
      </div>
    </div>
  </div>
);

// ─── Modal de sélection de projet ────────────────────────────────────────────

const UseTemplateModal: React.FC<{
  template: TemplateData;
  onClose: () => void;
  onSuccess: (projectId: string, archId: string) => void;
}> = ({ template, onClose, onSuccess }) => {
  const [projects, setProjects]             = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [newProjectName, setNewProjectName] = useState('');
  const [creating, setCreating]             = useState(false);
  const [error, setError]                   = useState<string | null>(null);

  useEffect(() => {
    getProjects()
      .then(list => { setProjects(list); if (list.length === 0) setSelectedProjectId('new'); })
      .catch(() => setError('Impossible de charger les projets.'))
      .finally(() => setLoadingProjects(false));
  }, []);

  const canSubmit = selectedProjectId === 'new'
    ? newProjectName.trim().length > 0
    : selectedProjectId !== '';

  const handleCreate = async () => {
    if (!canSubmit) return;
    setCreating(true);
    setError(null);
    try {
      let projectId = selectedProjectId;

      if (selectedProjectId === 'new') {
        const project = await createProject({
          name: newProjectName.trim(),
          provider: template.provider,
        });
        projectId = project.id;
      }

      const arch = await createArchitecture({
        projectId,
        name: template.name,
        description: template.description,
        provider: template.provider as any,
        diagramData: template.diagramData,
      });

      onSuccess(projectId, arch.id);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Une erreur est survenue.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Choisir un projet</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Le template <strong>{template.name}</strong> sera ajouté comme nouvelle architecture.
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition">
            <FiX size={18} />
          </button>
        </div>

        {/* Contenu */}
        <div className="p-6">
          {loadingProjects ? (
            <div className="flex items-center justify-center py-10 text-gray-400">
              <FiLoader className="animate-spin mr-2" size={20} />
              Chargement des projets…
            </div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
              {/* Projets existants */}
              {projects.map(p => (
                <button
                  key={p.id}
                  onClick={() => setSelectedProjectId(p.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition
                    ${selectedProjectId === p.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                >
                  <FiFolder className={selectedProjectId === p.id ? 'text-blue-600' : 'text-gray-400'} size={18} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-gray-900 truncate">{p.name}</div>
                  </div>
                  {selectedProjectId === p.id && <FiCheckCircle className="text-blue-600 flex-shrink-0" size={16} />}
                </button>
              ))}

              {/* Option nouveau projet */}
              <button
                onClick={() => setSelectedProjectId('new')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition
                  ${selectedProjectId === 'new'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                  }`}
              >
                <FiPlus className={selectedProjectId === 'new' ? 'text-blue-600' : 'text-gray-400'} size={18} />
                <span className={`text-sm font-medium ${selectedProjectId === 'new' ? 'text-blue-700' : 'text-gray-500'}`}>
                  Créer un nouveau projet
                </span>
              </button>
            </div>
          )}

          {/* Champ nom du nouveau projet */}
          {selectedProjectId === 'new' && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom du projet</label>
              <input
                type="text"
                placeholder="Mon projet cloud"
                value={newProjectName}
                onChange={e => setNewProjectName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />
            </div>
          )}

          {/* Erreur */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 text-red-700 text-sm rounded-lg border border-red-200">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 flex gap-3">
          <button
            onClick={onClose}
            disabled={creating}
            className="flex-1 px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition text-sm font-medium disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={handleCreate}
            disabled={!canSubmit || creating}
            className="flex-1 px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {creating ? (
              <><FiLoader className="animate-spin" size={14} /> Création…</>
            ) : (
              <><FiChevronRight size={16} /> Ouvrir dans le canvas</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplatesPage;
